import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus,
  Edit,
  Trash2,
  ClipboardList,
  Check,
  X,
  ChevronRight,
} from "lucide-react";

const MatricesPage = () => {
  const { authAxios, user } = useAuth();
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMatrix, setEditingMatrix] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    criteria: [],
  });
  const [newCriterion, setNewCriterion] = useState({
    name: "",
    description: "",
    weight: 10,
    options: [],
  });

  const fetchMatrices = async () => {
    try {
      const response = await authAxios.get("/matrices");
      setMatrices(response.data);
    } catch (error) {
      console.error("Error fetching matrices:", error);
      toast.error("Error al cargar matrices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrices();
  }, []);

  const resetForm = () => {
    setFormData({ name: "", description: "", criteria: [] });
    setNewCriterion({ name: "", description: "", weight: 10, options: [] });
    setEditingMatrix(null);
  };

  const handleOpenDialog = (matrix = null) => {
    if (matrix) {
      setEditingMatrix(matrix);
      setFormData({
        name: matrix.name,
        description: matrix.description,
        criteria: matrix.criteria || [],
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const addCriterion = () => {
    if (!newCriterion.name) {
      toast.error("El criterio debe tener un nombre");
      return;
    }
    setFormData({
      ...formData,
      criteria: [...formData.criteria, { ...newCriterion, id: Date.now().toString() }],
    });
    setNewCriterion({ name: "", description: "", weight: 10, options: [] });
  };

  const removeCriterion = (index) => {
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      if (editingMatrix) {
        await authAxios.put(`/matrices/${editingMatrix.id}`, formData);
        toast.success("Matriz actualizada");
      } else {
        await authAxios.post("/matrices", formData);
        toast.success("Matriz creada");
      }
      handleCloseDialog();
      fetchMatrices();
    } catch (error) {
      toast.error("Error al guardar la matriz");
    }
  };

  const handleDelete = async (matrixId) => {
    try {
      await authAxios.delete(`/matrices/${matrixId}`);
      toast.success("Matriz eliminada");
      fetchMatrices();
    } catch (error) {
      toast.error("Error al eliminar la matriz");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Matrices de Evaluación</h1>
          <p className="text-slate-500 mt-1">
            Define los criterios para evaluar la calidad de las llamadas
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sky-600 hover:bg-sky-700"
              onClick={() => handleOpenDialog()}
              data-testid="create-matrix-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Matriz
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMatrix ? "Editar Matriz" : "Nueva Matriz de Evaluación"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre de la Matriz</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Evaluación Servicio al Cliente"
                    data-testid="matrix-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción de la matriz..."
                    rows={3}
                    data-testid="matrix-description-input"
                  />
                </div>
              </div>

              {/* Criteria Section */}
              <div className="space-y-4">
                <Label>Criterios de Evaluación</Label>
                
                {formData.criteria.length > 0 && (
                  <div className="space-y-2">
                    {formData.criteria.map((criterion, index) => (
                      <div
                        key={criterion.id || index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{criterion.name}</p>
                          {criterion.description && (
                            <p className="text-sm text-slate-500">{criterion.description}</p>
                          )}
                          <Badge variant="outline" className="mt-1">
                            Peso: {criterion.weight}%
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCriterion(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new criterion */}
                <div className="p-4 border border-dashed border-slate-300 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-slate-600">Agregar Criterio</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Nombre del criterio"
                      value={newCriterion.name}
                      onChange={(e) => setNewCriterion({ ...newCriterion, name: e.target.value })}
                      data-testid="criterion-name-input"
                    />
                    <Input
                      type="number"
                      placeholder="Peso (%)"
                      value={newCriterion.weight}
                      onChange={(e) => setNewCriterion({ ...newCriterion, weight: parseInt(e.target.value) || 0 })}
                      min={0}
                      max={100}
                      data-testid="criterion-weight-input"
                    />
                  </div>
                  <Input
                    placeholder="Descripción del criterio"
                    value={newCriterion.description}
                    onChange={(e) => setNewCriterion({ ...newCriterion, description: e.target.value })}
                    data-testid="criterion-description-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCriterion}
                    className="w-full"
                    data-testid="add-criterion-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Criterio
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="save-matrix-btn">
                  {editingMatrix ? "Guardar Cambios" : "Crear Matriz"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Matrices Grid */}
      {matrices.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay matrices creadas</h3>
            <p className="text-slate-500 mb-4">
              Crea tu primera matriz de evaluación para comenzar a analizar llamadas
            </p>
            <Button onClick={() => handleOpenDialog()} className="bg-sky-600 hover:bg-sky-700">
              <Plus className="w-4 h-4 mr-2" />
              Crear Matriz
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matrices.map((matrix) => (
            <Card
              key={matrix.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`matrix-card-${matrix.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold line-clamp-1">
                      {matrix.name}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                      {matrix.description || "Sin descripción"}
                    </p>
                  </div>
                  {matrix.is_active && (
                    <Badge className="bg-green-100 text-green-700 ml-2">Activa</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Criterios</span>
                    <span className="font-medium">{matrix.criteria?.length || 0}</span>
                  </div>
                  
                  {matrix.criteria && matrix.criteria.length > 0 && (
                    <div className="space-y-1">
                      {matrix.criteria.slice(0, 3).map((criterion, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded"
                        >
                          <span className="text-slate-600 truncate">{criterion.name}</span>
                          <Badge variant="outline" className="text-xs ml-2">
                            {criterion.weight}%
                          </Badge>
                        </div>
                      ))}
                      {matrix.criteria.length > 3 && (
                        <p className="text-xs text-slate-400 text-center">
                          +{matrix.criteria.length - 3} más
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-slate-400">
                      {matrix.created_at ? format(new Date(matrix.created_at), "dd MMM yyyy", { locale: es }) : "-"}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(matrix)}
                        data-testid={`edit-matrix-${matrix.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {user?.role === "admin" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-matrix-${matrix.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar matriz?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. La matriz "{matrix.name}" será eliminada permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(matrix.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatricesPage;
